<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 full-height-page-wrapper">
        <Preloader v-if="isDataPageLoading" class="large-spinner" />

        <div class="page-table-header">
          <PageHeader showBreadcrumbs page-title="Sales report">
            <template #buttons>
            <div style="display: none;">test</div>
             <div class="d-flex gap-24">
                <DatePeriodNavigation
                  :start-date="new Date(_toIsoStringTime(selectedYm))"
                  :disable-prev="disablePrevMonth"
                  :disable-next="disableNextMonth"
                  :disabled-start-dates="{
                    to: new Date(fromDateToUTC(fromYm, 'YYYY-MM')),
                    from: new Date(),
                  }"
                  :date-format="config.dateFormatDatePicker"
                  :single-mode="true"
                  minimum-view="month"
                  datepicker-position="left"
                  @changePeriod="changeMonth"
                  @selectDate="selectMonth"
                />
                <b-button class="button" :variant="isShowAllFields ? 'secondary' : 'info'"
                  @click.prevent="toggleShowAllFields">
                  {{ isShowAllFields ? "Collapse all" : "Expand all" }}
                </b-button>
              </div>
            </template>
          </PageHeader>
        </div>

        <div class="full-height-content">
          <div v-if="reportDataBySelectedYM.itemsByManager.length > 0 && !isDataPageLoading">
            <div class="d-flex flex-column gap-20">
              <TableSalesReportItem v-for="managerData in reportDataBySelectedYM.itemsByManager"
                :key="`${managerData.managerId}`" :ref="`manager-${managerData.managerId}`" :data="managerData" :currentFields="getCurrentFields"  :isShowAllFields="isShowAllFields" :preSalesCommissionFormulas="reportDataBySelectedYM.preSalesCommissionFormulas"
                @showEditForm="showEditInvoice" @reloadInvoices="reloadInvoices" @getSlideOut="getSlideOut" @stateChanged="onComponentStateChanged" />
            </div>

            <!-- Grand Total Section - aligned with table columns -->
            <div class="grand-total-section">
              <div class="grand-total-grid" :class="$can(permissions.ReadAllComments) ? 'with-comments' : 'without-comments'">
                <!-- Grand Total label spans multiple columns -->
                <div class="grand-total-cell"></div>

                <!-- Empty Employee column -->
                <div class="grand-total-cell grand-total-name-cell">
                  <span class="grand-total-label">GRAND TOTAL</span>
                </div>

                <!-- Planned $ total -->
                <div class="grand-total-cell grand-total-planned">
                  <span class="grand-total-amount">{{ getGrandTotalPlanned() }}</span>
                </div>

                <!-- Paid $ total -->
                <div class="grand-total-cell grand-total-paid">
                  <span class="grand-total-amount" :class="getGrandTotalPaidAmountClass()"
                        v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="getGrandTotalPaidByTooltipHtml()">{{ getGrandTotalPaid() }}</span>
                </div>

                <!-- Empty Commission column -->
                <div class="grand-total-cell"></div>

                <!-- Empty Invoices column -->
                <div class="grand-total-cell"></div>
              </div>
            </div>
          </div>

          <EmptyStateNoResults
            v-else-if="reportDataBySelectedYM.itemsByManager.length === 0 && !isDataPageLoading"
            title="No sales report yet"
            description="No sales data available for this period. Once sales are recorded, your report will appear here."
            :customSvg="salesReportSvg"
            :withoutButton="true"
          />
        </div>

        <FinanceInvoicesForm v-model="invoiceFormData.show" :mode="invoiceFormData.mode" :data="invoiceFormData.form"
          :title="invoiceFormData.title" :headerTitle="invoiceFormData.headerTitle"
          :submitButtonText="invoiceFormData.submitButtonText" :bankDetails="getOwnBankDetails" :templates="getTemplates"
          :projects="getActualProjects" :statuses="getStatuses" :types="getInvoicesTypes"
          :breadcrumbs="salesReportBreadcrumbs"
          @submit="saveInvoice" @invoiceFormClosed="onClose" @showDownloadModal="showDownloadInvoiceModal" @loadingChange="handleInvoiceFormLoadingChange" ref="financeInvoicesForm" inModal />

        <!-- Download Invoice Modal -->
        <DownloadInvoiceModal
          v-model="downloadInvoiceModal.show"
          :invoice="downloadInvoiceModal.invoice"
          :templates="getTemplates"
          :triggerElement="downloadInvoiceModal.triggerElement"
        />

        <SlideOutContainerViaPortal className="sales-report-slideout">
          <div v-if="showSlideOut">
            <TableWithDetails ref="tableWithDetails" :current-project="openProject" :breadcrumbs="slideoutBreadcrumbs" :invoiceFormBreadcrumbs="nestedInvoiceFormBreadcrumbs" :customOnClose="handleNestedInvoiceFormClose" :customSaveInvoice="handleNestedInvoiceSave" :showCloseButton="true" :closeButtonHandler="closeSlideOut" />
          </div>
        </SlideOutContainerViaPortal>
      </div>
    </div>
  </div>
</template>

<script>
import { commentEntityTypes } from '@mocks/comment-entity-types'
import PageHeader from '@components/PageHeader'
import Moment from 'moment'
import { permissions } from '@mocks/permissions'
import { mapActions, mapGetters } from 'vuex'
import InvoiceFormModal from '@/mixins/InvoiceFormModal'
import ScrollPositionMixin from '@/mixins/ScrollPositionMixin'
import Preloader from '@components/Preloader'
import TableSalesReportItem from '@components/tables/TableSalesReportItem'
import FinanceInvoicesForm from '@components/modals/FinanceInvoicesForm/FinanceInvoicesForm.vue'
import DownloadInvoiceModal from '@components/modals/DownloadInvoiceModal.vue'
import { cloneDeep } from 'lodash'
import EmptyStateNoResults from '@components/EmptyStateNoResults.vue'
import { salesReportSvg } from '@/assets/icons/salesReportIcon.js'
import SlideOutContainerViaPortal from '@components/transition/SlideOutContainerViaPortal.vue'
import TableWithDetails from '@components/tables/TableWithDetails.vue'
import DatePeriodNavigation from '@components/DatePeriodNavigation.vue'

const currentYm = Moment().format('YYYY-MM')

export default {
  components: {
    PageHeader,
    Preloader,
    TableSalesReportItem,
    FinanceInvoicesForm,
    EmptyStateNoResults,
    SlideOutContainerViaPortal,
    TableWithDetails,
    DatePeriodNavigation,
    DownloadInvoiceModal
  },
  mixins: [InvoiceFormModal, ScrollPositionMixin],
  data () {
    const commentField = this.$can(permissions.ReadAllComments) ? [
      {
        key: 'comment',
        label: 'Comment',
        width: 190,
        minWidth: 190
      }
    ] : []

    return {
      permissions,
      reportData: {},
      reportDataBySelectedYM: {
        itemsByManager: [],
        preSalesCommissionFormulas: []
      },
      isDataPageLoading: false,
      config: {
        dateFormat: 'YYYY-MM',
        dateFormatDatePicker: 'yyyy-MM'
      },
      selectedYm: currentYm,
      fromYm: '2021-02',
      salesReportSvg: salesReportSvg,
      downloadInvoiceModal: {
        show: false,
        invoice: null,
        triggerElement: null
      },
      showSlideOut: false,
      openProject: null,
      isUpdatingFromButtonClick: false, // Flag to distinguish button clicks from automatic state updates
      currentFields: [
        {
          key: 'info',
          label: 'Projects',
          width: 120
        },
        {
          key: 'channel',
          label: 'Channel',
          width: 80
        },
        {
          key: 'pre_sales',
          label: 'Pre sales',
          width: 85
        },
        ...commentField,
        {
          key: 'employee',
          label: 'Employee',
          width: 195,
          minWidth: 195
        },
        {
          key: 'planned',
          label: 'Planned $',
          class: 'planned-total',
          width: 90
        },
        {
          key: 'paid',
          label: 'Paid $',
          class: 'paid-total',
          width: 90
        },
        {
          key: 'commission',
          label: 'Comm',
          width: 60
        },
        {
          key: 'invoices',
          label: 'Invoices',
          width: 120
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      isShowAllFields: 'finances/getIsShowAllFields'
    }),
    commentEntityTypes () {
      return commentEntityTypes
    },
    disableNextMonth () {
      return this.selectedYm === currentYm
    },
    disablePrevMonth () {
      return this.fromYm === this.selectedYm
    },
    getCurrentFields () {
      const currentTableFields = cloneDeep(this.currentFields)
      // Since we removed the planned_by_employee and paid_by_employee columns,
      // we now always return the same field structure regardless of isShowAllFields
      return currentTableFields
    },
    salesReportBreadcrumbs () {
      return [
        { text: 'Home', to: { name: 'home' } },
        { text: 'Sales report',
          click: this.handleSalesReportBreadcrumbClick
        }
      ]
    },
    slideoutBreadcrumbs () {
      return [
        { text: 'Home', to: { name: 'home' } },
        { text: 'Sales report',
          click: this.handleSlideoutBreadcrumbClick
        }
      ]
    },
    nestedInvoiceFormBreadcrumbs () {
      const projectName = this.openProject?.name || this.openProject?.key || ''
      return [
        { text: 'Home', to: { name: 'home' } },
        { text: 'Sales report', click: this.handleNestedBreadcrumbToSalesReport },
        { text: projectName, click: this.handleNestedBreadcrumbToHoursReport }
      ]
    }
  },
  watch: {
    selectedYm: async function (dateYm) {
      if (this.reportData[dateYm] === undefined) {
        this.reportData[dateYm] = await this.fetchSalesReportData(dateYm)
      }
      this.reportDataBySelectedYM = this.reportData[dateYm]
      // Apply stored button state to new components after data loads
      this.$nextTick(() => {
        this.applyStoredStateToComponents()
      })
    },
    isShowAllFields (newValue) {
      // Only apply changes to all components if this is triggered by a button click
      // not by automatic state updates from manual card/project toggles
      if (!this.isUpdatingFromButtonClick) {
        return
      }

      // Call methods on all TableSalesReportItem components
      this.$nextTick(() => {
        this.reportDataBySelectedYM.itemsByManager.forEach(managerData => {
          const ref = `manager-${managerData.managerId}`
          const component = this.$refs[ref]
          if (component && component[0]) {
            if (newValue) {
              // Expand manager cards and expand all projects
              component[0].expandManagerCard()
              component[0].expandAllProjects()
            } else {
              // Collapse manager cards and collapse all projects
              component[0].collapseManagerCard()
              component[0].collapseAllProjects()
            }
          }
        })

        // Reset the flag after processing
        this.isUpdatingFromButtonClick = false
      })
    }
  },
  async created () {
    this.reportData[this.selectedYm] = await this.fetchSalesReportData(
      this.selectedYm
    )
    this.reportDataBySelectedYM = this.reportData[this.selectedYm]
    await this.fetchInvoiceFormData()
    // Apply stored button state to initial components
    this.$nextTick(() => {
      this.applyStoredStateToComponents()
    })
  },
  methods: {
    ...mapActions({
      fetchComments: 'comments/fetchComments'
    }),
    selectMonth (data, type) {
      this.selectedYm = this._toLocalTime(data, this.config.dateFormat)
    },
    changeMonth (type) {
      const currentMonth = Moment(this.selectedYm, this.config.dateFormat)
      let newYm

      if (type === 'next') {
        newYm = currentMonth.add(1, 'months').format(this.config.dateFormat)
      } else {
        newYm = currentMonth
          .subtract(1, 'months')
          .format(this.config.dateFormat)
      }

      this.selectedYm = newYm
    },
    async fetchSalesReportData (dateYm) {
      return this._toggleDataPageLoading(async () => {
        if (this.$can(permissions.ReadAllComments)) {
          this.fetchComments({ type: commentEntityTypes.SalesReportProject })
        }
        return this.$api.finances
          .getReportsSales({ dateYm })
          .then((response) => {
            return response?.data
          })
      })
    },
    async reloadInvoices () {
      this.reportData = {}
      this.reportData[this.selectedYm] = await this.fetchSalesReportData(
        this.selectedYm
      )
      this.reportDataBySelectedYM = this.reportData[this.selectedYm]
      // Apply stored button state after reload
      this.$nextTick(() => {
        this.applyStoredStateToComponents()
      })
    },
    hideInvoiceForm () {
      this.invoiceFormData.show = false
      // Only navigate if not already on the correct route
      if (this.$route.name !== 'finances-sales-report') {
        this.$router.push({ name: 'finances-sales-report' }).catch(() => {})
      }
    },
    onClose () {
      // Restore URL to current route (similar to TableWithDetails.vue)
      const route = this.$router.resolve({
        name: this.$route.name,
        params: this.$route.params,
        query: this.$route.query
      })
      window.history.pushState({}, '', route.href)

      this.hideInvoiceForm()
      // Restore scroll position after modal closes with a small delay
      setTimeout(() => {
        this.restoreScrollPosition()
      }, 300)
    },
    getGrandTotalPlanned () {
      const total = this.reportDataBySelectedYM.itemsByManager.reduce(
        (sum, managerData) => {
          return sum + managerData.projects.reduce(
            (managerSum, project) => managerSum + project.plannedAmount,
            0
          )
        },
        0
      )
      return this.roundValue(total)
    },
    getGrandTotalPaid () {
      const total = this.reportDataBySelectedYM.itemsByManager.reduce(
        (sum, managerData) => {
          return sum + managerData.projects.reduce(
            (managerSum, project) => managerSum + project.paidAmount,
            0
          )
        },
        0
      )
      return this.roundValue(total)
    },
    getGrandTotalPaidAmountClass () {
      const totalPaid = this.getGrandTotalPaid()
      const totalPlanned = this.getGrandTotalPlanned()

      if (totalPaid < totalPlanned) {
        return 'paid-amount-partial'
      } else if (totalPaid >= totalPlanned) {
        return 'paid-amount-complete'
      }
      return ''
    },
    getGrandTotalPaidByTooltipHtml () {
      const totalPaid = this.getGrandTotalPaid()
      const totalPlanned = this.getGrandTotalPlanned()
      const notPaid = this.roundValue(totalPlanned - totalPaid)
      return `<div class="text-left">
        <strong>Grand total</strong><br>
        Paid: ${totalPaid}<br>
        Not paid: ${notPaid}
      </div>`
    },
    getSlideOut (project) {
      this.scrollToTheTop()
      this.showSlideOut = true
      this.openProject = {
        id: project.projectId,
        name: project.projectName,
        key: project.projectKey
      }
      // Update URL to include project ID without navigation
      const newUrl = `/finances-reports-hours-and-payments/${project.projectId}`
      window.history.pushState({}, '', newUrl)
      window.scrollTo(0, 0)
    },
    closeSlideOut () {
      this.showSlideOut = false
      this.openProject = null
      // Restore URL back to sales report
      window.history.pushState({}, '', '/finances-sales-report')
      // Restore scroll position after modal closes with a small delay
      setTimeout(() => {
        this.restoreScrollPosition()
      }, 300)
    },
    handleSalesReportBreadcrumbClick () {
      this.hideInvoiceForm()
      // Close all invoice popups when navigating via breadcrumb
      this.reportDataBySelectedYM.itemsByManager.forEach(managerData => {
        const ref = `manager-${managerData.managerId}`
        const component = this.$refs[ref]
        if (component && component[0] && component[0].closeAllPopups) {
          component[0].closeAllPopups()
        }
      })
      // Always navigate to clean up the URL (invoice form may have changed it)
      this.$router.push({ name: 'finances-sales-report' }).catch(() => {})
      // Restore scroll position after modal closes with a small delay
      setTimeout(() => {
        this.restoreScrollPosition()
      }, 300)
    },
    handleSlideoutBreadcrumbClick () {
      this.closeSlideOut()
      // Always navigate to clean up the URL
      this.$router.push({ name: 'finances-sales-report' }).catch(() => {})
      // Restore scroll position after modal closes with a small delay
      setTimeout(() => {
        this.restoreScrollPosition()
      }, 300)
    },
    handleNestedBreadcrumbToSalesReport () {
      // Close invoice form and slideout, go back to sales report
      this.hideInvoiceForm()
      // Close slideout without scroll restoration (since we're navigating away)
      this.showSlideOut = false
      this.openProject = null
      // Close all invoice popups when navigating via breadcrumb
      this.reportDataBySelectedYM.itemsByManager.forEach(managerData => {
        const ref = `manager-${managerData.managerId}`
        const component = this.$refs[ref]
        if (component && component[0] && component[0].closeAllPopups) {
          component[0].closeAllPopups()
        }
      })
      this.$router.push({ name: 'finances-sales-report' }).catch(() => {})
    },
    handleNestedBreadcrumbToHoursReport () {
      // Close invoice form in TableWithDetails but keep hours and payments slideout open
      if (this.$refs.tableWithDetails && this.$refs.tableWithDetails.invoiceFormData) {
        this.$refs.tableWithDetails.invoiceFormData.show = false
        // Restore scroll position after modal closes with a small delay
        setTimeout(() => {
          this.restoreScrollPosition()
        }, 300)
      }

      // Also close the main page invoice form just in case
      this.hideInvoiceForm()

      // Restore URL to hours and payments with project ID
      if (this.openProject?.id) {
        const newUrl = `/finances-reports-hours-and-payments/${this.openProject.id}`
        window.history.pushState({}, '', newUrl)
      }
    },
    handleNestedInvoiceFormClose () {
      // Custom close handler for invoice form in hours and payments slideout
      // Close the invoice form but keep slideout open
      if (this.$refs.tableWithDetails && this.$refs.tableWithDetails.invoiceFormData) {
        this.$refs.tableWithDetails.invoiceFormData.show = false
      }

      // Close main page invoice form just in case
      this.invoiceFormData.show = false

      // Restore URL to hours and payments with project ID
      if (this.openProject?.id) {
        const newUrl = `/finances-reports-hours-and-payments/${this.openProject.id}`
        window.history.pushState({}, '', newUrl)
      }

      // Call restoreScrollPosition if available
      if (this.$refs.tableWithDetails && this.$refs.tableWithDetails.restoreScrollPosition) {
        this.$refs.tableWithDetails.restoreScrollPosition()
      }
    },
    async handleNestedInvoiceSave (fields) {
      // Custom save handler for nested invoice form
      // Save the invoice using the mixin's methods
      if (this.$refs.tableWithDetails) {
        const tableWithDetails = this.$refs.tableWithDetails
        if (tableWithDetails.invoiceFormData.form && tableWithDetails.invoiceFormData.form.id) {
          const { data } = await tableWithDetails.updateInvoice(fields)
          tableWithDetails.invoiceFormData.show = false
          tableWithDetails.invoiceFormData.item = data
        } else {
          const { data } = await tableWithDetails.createInvoice(fields)
          tableWithDetails.invoiceFormData.show = false
          tableWithDetails.invoiceFormData.item = data
        }

        // Restore URL to hours and payments with project ID
        if (this.openProject?.id) {
          const newUrl = `/finances-reports-hours-and-payments/${this.openProject.id}`
          window.history.pushState({}, '', newUrl)
        }
      }
    },
    async saveInvoice (fields) {
      try {
        if (this.invoiceFormData.form && this.invoiceFormData.form.id) {
          const { data } = await this.updateInvoice(fields)
          this.hideInvoiceForm()
          // Update URL back to sales report
          this.$router.push({ name: 'finances-sales-report' }).catch(() => {})
          // Reload sales report data to reflect changes
          await this.reloadInvoices()
          this.invoiceFormData.item = data
        } else {
          const { data } = await this.createInvoice(fields)
          this.hideInvoiceForm()
          // Update URL back to sales report
          this.$router.push({ name: 'finances-sales-report' }).catch(() => {})
          // Reload sales report data to reflect changes
          await this.reloadInvoices()
          this.invoiceFormData.item = data
        }
      } finally {
        // Always turn off loading state
        if (this.$refs.financeInvoicesForm) {
          this.$refs.financeInvoicesForm.finishLoading()
        }
      }
    },
    showDownloadInvoiceModal (data) {
      this.downloadInvoiceModal.invoice = data.invoice
      this.downloadInvoiceModal.triggerElement = data.triggerElement
      this.downloadInvoiceModal.show = true
    },
    handleInvoiceFormLoadingChange (loading) {
      // This method handles loading state changes from the invoice form
      // The loading state is now managed internally by the FinanceInvoicesForm component
    },
    roundValue (value) {
      return Math.round(value * 100) / 100
    },
    toggleShowAllFields () {
      this.isUpdatingFromButtonClick = true
      this.$store.dispatch('finances/setIsShowAllFields', !this.isShowAllFields)
    },
    applyStoredStateToComponents () {
      // Apply the stored button state to all components
      if (!this.reportDataBySelectedYM?.itemsByManager?.length) {
        return
      }

      // This is applying stored state, so it should trigger component changes
      this.isUpdatingFromButtonClick = true

      this.reportDataBySelectedYM.itemsByManager.forEach(managerData => {
        const ref = `manager-${managerData.managerId}`
        const component = this.$refs[ref]
        if (component && component[0]) {
          if (this.isShowAllFields) {
            // Apply expanded state
            component[0].expandManagerCard()
            component[0].expandAllProjects()
          } else {
            // Apply collapsed state (this is already default, but being explicit)
            component[0].collapseManagerCard()
            component[0].collapseAllProjects()
          }
        }
      })

      // Reset the flag after processing
      this.isUpdatingFromButtonClick = false
    },
    onComponentStateChanged () {
      // Called when individual manager cards or projects change state
      this.$nextTick(() => {
        this.checkAndUpdateButtonState()
      })
    },
    checkAndUpdateButtonState () {
      // Check the current state of all components to determine if button should be updated
      if (!this.reportDataBySelectedYM?.itemsByManager?.length) {
        return
      }

      const components = this.reportDataBySelectedYM.itemsByManager.map(managerData => {
        const ref = `manager-${managerData.managerId}`
        return this.$refs[ref]?.[0]
      }).filter(Boolean)

      if (components.length === 0) {
        return
      }

      // Check if ALL manager cards are expanded
      const allCardsExpanded = components.every(component => component.isCollapsed === true)

      // Check if ALL projects within expanded cards are also expanded
      let allProjectsExpanded = true
      if (allCardsExpanded) {
        components.forEach(component => {
          // Check if all expandable projects in this component are expanded
          const expandableProjects = component.expandableProjects || []
          const expandedProjects = component.expandedProjects || {}

          expandableProjects.forEach(project => {
            if (!expandedProjects[project.projectId]) {
              allProjectsExpanded = false
            }
          })
        })
      } else {
        // If not all cards are expanded, then not everything is expanded
        allProjectsExpanded = false
      }

      // Update button state based on whether EVERYTHING is expanded or not
      if (allCardsExpanded && allProjectsExpanded) {
        // Everything is fully expanded - button should show "Collapse all"
        if (!this.isShowAllFields) {
          this.$store.dispatch('finances/setIsShowAllFields', true)
        }
      } else {
        // Anything is collapsed - button should show "Expand all"
        if (this.isShowAllFields) {
          this.$store.dispatch('finances/setIsShowAllFields', false)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.sales-report_table {
  ::v-deep tr {
    th {
      text-transform: lowercase;
    }
  }
}

.grand-total-section {
  background-color: #fff;
  padding: 12px 15px;
  border-radius: 10px;
  margin-top: 20px;
  min-width: calc(#{$sales-table-min-width} + 30px);
}

.grand-total-grid {
  display: grid;
  align-items: center;
  width: 100%;
  padding: 0;

  // Grid layout for single row with grand total label and amounts
  &.with-comments {
    grid-template-columns: 1fr $sales-col-employee $sales-col-planned $sales-col-paid $sales-col-commission $sales-col-invoices; // Grand total spans first columns, then Employee, Planned, Paid, Commission, Invoices
  }

  &.without-comments {
    grid-template-columns: 1fr $sales-col-employee $sales-col-planned $sales-col-paid $sales-col-invoices; // Grand total spans first columns, then Employee, Planned, Paid, Invoices
  }
}

.grand-total-cell {
  display: flex;
  align-items: center;
  padding-right: 8px;

  &.grand-total-name-cell {
    justify-content: end;
    text-align: left;
  }

  &.grand-total-planned, &.grand-total-paid {
    justify-content: flex-end;
    text-align: right;
  }
}

.grand-total-label {
  font-family: Ubuntu;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  text-transform: uppercase;
}

// Base class for all amount indicators with common styling
.amount-indicator {
  border-radius: 3px;
  line-height: 20px;
}

.grand-total-amount {
  @extend .amount-indicator;
  font-family: Ubuntu;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
}

.paid-amount-partial {
  background-color: #FFDFC5;
  padding: 0 4px;
}

.paid-amount-complete {
  background-color: #C3E6CB;
  padding: 0 4px;
}

.sales-report-slideout:not(:has(.slide-out-container)) {
  z-index: calc(#{$navigationMenuZIndex} - 2) !important;
}
</style>
